import React, { useState, useEffect } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { api } from '../../services/api';
import ReportDateSelector, { DateRangeOption } from './ReportDateSelector';
import { useCurrency } from '../CurrencyContext';
import { CURRENCIES } from '../../constants';

interface ExecutiveGrowthReportProps {
  initialRange?: DateRangeOption;
}

const ExecutiveGrowthReport: React.FC<ExecutiveGrowthReportProps> = ({ initialRange = '90d' }) => {
  const [range, setRange] = useState<DateRangeOption>(initialRange);
  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState({
    netNewSubs: 0,
    churnRate: 0,
    expansionMrr: 0,
    netGrowth: 0,
    mrr: 0,
  });
  const { currencyCode, currencySymbol } = useCurrency();
  const activeRate = CURRENCIES.find(c => c.code === currencyCode)?.rate || 1;

  useEffect(() => {
    let isMounted = true;
    const fetchReport = async () => {
      try {
        const [lifecycle, growth, dashboard] = await Promise.all([
          api.analytics.getLifecycle(range),
          api.analytics.getGrowth(),
          api.analytics.getDashboard(),
        ]);
        if (isMounted) {
          if (lifecycle?.lifecycleData?.length) {
            setData(lifecycle.lifecycleData);
          }
          setStats({
            netNewSubs: lifecycle?.netSubscriberGrowth ?? dashboard?.totalCustomers ?? 0,
            churnRate: dashboard?.churnRate ?? 0,
            expansionMrr: Math.round((growth?.mrr || dashboard?.mrr || 0) * 0.15),
            netGrowth: growth?.arr || (dashboard?.mrr || 0) * 12,
            mrr: growth?.mrr || dashboard?.mrr || 0,
          });
        }
      } catch (err) {
        console.error('Failed to load growth report:', err);
      }
    };
    fetchReport();
    return () => { isMounted = false; };
  }, [range]);

  return (
    <div id="executive-growth-report" className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 bg-indigo-600 rounded-xl flex items-center justify-center text-white">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 3v18h18M19 9l-5 5-4-4-3 3" /></svg>
            </div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">Executive Growth Summary</h2>
          </div>
          <p className="text-slate-500 text-sm">Live correlation of subscriber volume and revenue velocity.</p>
        </div>
        <div className="flex items-center gap-3">
          <ReportDateSelector value={range} onChange={setRange} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Net Subscriber Growth</p>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-black text-slate-900">+{stats.netNewSubs.toLocaleString()}</p>
            <span className="text-emerald-600 text-xs font-bold">Active</span>
          </div>
        </div>
        <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Churn Rate</p>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-black text-slate-900">{stats.churnRate}%</p>
            <span className="text-slate-500 text-xs font-semibold">Monthly</span>
          </div>
        </div>
        <div className="p-5 bg-indigo-50/60 rounded-2xl border border-indigo-100">
          <p className="text-[10px] font-black text-indigo-500 uppercase tracking-wider mb-1">Monthly Run-Rate</p>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-black text-indigo-900">{currencySymbol}{Math.floor(stats.mrr * activeRate).toLocaleString()}</p>
          </div>
        </div>
        <div className="p-5 bg-slate-900 rounded-2xl shadow-lg shadow-slate-200">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Annualized (ARR)</p>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-black text-white">{currencySymbol}{Math.floor(stats.netGrowth * activeRate).toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className="h-[380px] w-full">
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-slate-400 text-sm">Loading growth metrics...</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#64748b', fontWeight: 600}} dy={10} />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{fontSize: 11, fill: '#64748b'}} />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{fontSize: 11, fill: '#64748b'}} tickFormatter={(v) => `${currencySymbol}${Math.floor((v * activeRate)/1000)}k`} />
              <Tooltip
                contentStyle={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.05)', padding: '12px' }}
                formatter={(value: number, name: string) => {
                  const converted = Math.floor(value * activeRate);
                  if (name.includes('MRR') || name.includes('Revenue')) return [`${currencySymbol}${converted.toLocaleString()}`, name];
                  return [value.toLocaleString(), name];
                }}
              />
              <Legend verticalAlign="top" align="right" height={36} iconType="circle" wrapperStyle={{ fontSize: '11px', fontWeight: 700 }} />
              <Bar yAxisId="left" dataKey="newSubs" name="New Subscriptions" fill="#10b981" radius={[4, 4, 0, 0]} barSize={28} />
              <Bar yAxisId="left" dataKey="churned" name="Churned" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={28} />
              <Line yAxisId="left" type="monotone" dataKey="active" name="Active Subscribers" stroke="#6366f1" strokeWidth={3} dot={{ r: 4, fill: '#6366f1' }} />
              <Line yAxisId="right" type="monotone" dataKey="mrr" name="MRR Velocity" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default ExecutiveGrowthReport;
